import {useTranslation, Trans} from '@/translations/i18n.tsx'
import {Assignment, AssignmentUrlParams, Space} from '@features/assignment/types.ts'
import {useState} from 'react'
import {useURLParsedParams} from '@hooks/useURLParsedParams'
import {useGetSpaces} from '@features/assignment/queries/useGetSpaces'
import {AxiosError} from 'axios'
import {ErrorBox} from '@components/commons/error-box'
import {AlertTriangleIcon} from '@components/ui/icon'
import {Skeleton} from '@components/ui/skeleton'
import {AssignmentCardLink} from '@features/assignment/components/assignment-card-link'
import {Checkbox} from '@components/commons/checkbox'
import {TASK_CATEGORY_HOTEL} from '@features/assignment/utils'
import {
    LOCALSTORAGE_KEY_SHOOTLIST_CARD_PREFIX,
    SPACE_KEY_TO_LABEL,
    ASSIGNMENT_CARD_WRAPPER_CLASSNAME,
    ASSIGNMENT_CARD_TITLE_CLASSNAME,
    ASSIGNMENT_CARD_SUBTITLE_CLASSNAME
} from '@features/assignment/constants'

export const AssignmentShootlistCard = ({assignment}: {assignment: Assignment}) => {
    const {t} = useTranslation()
    const urlParams = useURLParsedParams(AssignmentUrlParams)
    const storedFlags = localStorage.getItem(`${LOCALSTORAGE_KEY_SHOOTLIST_CARD_PREFIX}-${assignment.id}`)

    const [currentFlags, setCurrentFlags] = useState(storedFlags ? JSON.parse(storedFlags) : [])

    const assignmentSpacesQuery = useGetSpaces(assignment.id, {
        enabled: urlParams.success
    })
    const isHotel = assignment.task_category.name === TASK_CATEGORY_HOTEL
    const photoshootChecklistLink = assignment.photoshoot_checklist_pdf_link

    const onChangeShootlistFlag = (flag: Space['space']['key_name']) => {
        const updatedFlags = currentFlags.includes(flag)
            ? currentFlags.filter((parsedFlag: Space['space']['key_name']) => parsedFlag != flag)
            : currentFlags.concat(flag)

        localStorage.setItem(`${LOCALSTORAGE_KEY_SHOOTLIST_CARD_PREFIX}-${assignment.id}`, JSON.stringify(updatedFlags))
        setCurrentFlags(updatedFlags)
    }

    if (assignmentSpacesQuery.isPending) {
        return (
            <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
                <Skeleton height={28} />
                {Array.from({length: 10}).map((_, index) => (
                    <Skeleton key={index} />
                ))}
            </div>
        )
    }

    if (
        !urlParams.success ||
        (assignmentSpacesQuery.error instanceof AxiosError && assignmentSpacesQuery.error.response?.status == 404) ||
        !assignmentSpacesQuery.data
    ) {
        return (
            <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
                <ErrorBox icon={<AlertTriangleIcon />} title={t('spaces:not_found')} />
            </div>
        )
    }

    if (assignmentSpacesQuery.isError) {
        return (
            <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
                <ErrorBox icon={<AlertTriangleIcon />} title={t('errors:default')} />
            </div>
        )
    }

    if (assignmentSpacesQuery.data && assignmentSpacesQuery.data.length == 0) {
        return (
            <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
                <h3 className={ASSIGNMENT_CARD_TITLE_CLASSNAME}>{t('assignment:shootlist_card:title')}</h3>
                <AssignmentCardLink taskCategoryName={assignment.task_category.name} pdfLink={assignment?.pdf_link} />
            </div>
        )
    }

    return (
        <div className={ASSIGNMENT_CARD_WRAPPER_CLASSNAME}>
            <div className="flex flex-col gap-1">
                <h3 className={ASSIGNMENT_CARD_TITLE_CLASSNAME}>{t('assignment:shootlist_card:title')}</h3>

                <p className={ASSIGNMENT_CARD_SUBTITLE_CLASSNAME}>{t('assignment:shootlist_card:subtitle')}</p>
            </div>

            <ul className="flex flex-col gap-4.5 w-full px-2">
                {assignmentSpacesQuery.data.map(assignmentSpace => (
                    <li className="flex items-center gap-4 w-full whitespace-nowrap" key={assignmentSpace.id}>
                        <Checkbox
                            id={assignmentSpace.id.toString()}
                            containerClass="[&_label]:text-md [&_small]:block md:[&_small]:hidden"
                            label={t(SPACE_KEY_TO_LABEL[assignmentSpace.space.key_name].label, {
                                value: SPACE_KEY_TO_LABEL[assignmentSpace.space.key_name]?.value
                            })}
                            onCheckedChange={() => onChangeShootlistFlag(assignmentSpace.space.key_name)}
                            checked={currentFlags.includes(assignmentSpace.space.key_name)}
                            // Visible only on mobile:
                            helpText={t('assignment:spaces:minimum_images', {
                                count: assignmentSpace.configuration_space.raws_minimum_images
                            })}
                        />

                        <span className="hidden h-px w-full bg-size-[8px_1px] bg-bottom bg-repeat-x bg-linear-to-r from-neutral-300 from-33% to-white to-0% md:block" />

                        <p className="hidden text-neutral-600 md:block">
                            {t('assignment:spaces:minimum_images', {
                                count: assignmentSpace.configuration_space.raws_minimum_images
                            })}
                        </p>
                    </li>
                ))}
            </ul>

            {isHotel && photoshootChecklistLink && (
                <div className="flex items-center gap-1 text-sm [&_a]:cursor-pointer [&_a]:font-medium [&_a]:text-purple-700 [&_a]:underline">
                    {t('commons:view')}
                    <Trans
                        i18nKey={'assignment:photoshoot_checklist_link'}
                        components={{
                            a: <a href={photoshootChecklistLink} target="_blank" />
                        }}
                    />
                </div>
            )}

            <AssignmentCardLink taskCategoryName={assignment.task_category.name} pdfLink={assignment?.pdf_link} />
        </div>
    )
}
